import { Component, EventEmitter, OnInit, OnDestroy, Output, ChangeDetectorRef, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { LocalChartDeleteService } from './local-chart-delete.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';
import * as _ from 'lodash-es';

@Component({
    selector: 'gtw-local-chart-delete',
    templateUrl: './local-chart-delete.component.html',
    styleUrls: ['./local-chart-delete.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class LocalChartDeleteComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();

  filterParams: any = {};
  baseURLs: any = {};
  rowData: any = {};

  modalConfig: ConfirmationModalConfig = {
    title: 'Confirm',
    message: 'Are you sure you want to Delete this Chart?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast',
    loading: false,
    loadingText: 'Processing...'
  };

  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};

  showModal: boolean = false;
  crudLoading: boolean = false;
  logged_in_user: string = '';

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private localChartDeleteService: LocalChartDeleteService
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void { this.subs.unsubscribe(); }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = header.globalParams || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.cdr.detectChanges();
  }

  private resolveApiBase(): string {
    return this.GENERAL_CONFIG?.base_url || this.baseURLs?.api || this.baseURLs?.base_url || '';
  }

  init(): void {
    if (this.baseURLs && Object.keys(this.baseURLs).length > 0 &&
        this.filterParams && Object.keys(this.filterParams).length > 0 &&
        this.GENERAL_CONFIG && Object.keys(this.GENERAL_CONFIG).length > 0 &&
        this.resolveApiBase()) {
      this.initializeData();
    }
  }

  initializeData(): void {
    // Get logged in user from USER_SETTINGS like fir163jsync
    if (this.USER_SETTINGS?.user?.sso_id) {
      this.logged_in_user = this.USER_SETTINGS.user.sso_id;
    }
  }

  onConfirm(): void {
    this.confirmDelete();
  }

  cancel(): void {
    // Following fir163jsync pattern - dispatch CustomEvent to close modal
    this.showModal = false;

    const updateEvent = new CustomEvent('request-grid-update', {
      detail: 'cancel',
      bubbles: true
    });

    if (document.querySelector('gtw-local-chart-delete-ce')) {
      document.querySelector('gtw-local-chart-delete-ce')?.dispatchEvent(updateEvent);
    }
  }

  onCancel(): void {
    // Called from confirmation modal - delegates to cancel()
    this.cancel();
  }

  confirmDelete(): void {
    // Follow exact AngularJS confirm_delete function behavior
    if (this.crudLoading) {
      this.gtwSnackbarService.openSnackBar('Please wait while we save this request', 'gtw-snackbar--yellow');
      return;
    }

    this.crudLoading = true;
    this.modalConfig = {...this.modalConfig, loading: true};

    // Build delete object exactly like AngularJS
    const dummyJson: any = {};
    dummyJson["object_id"] = this.rowData?.object_id || '';
    dummyJson['local_chart_key'] = this.rowData?.CHART_KEY || '';
    dummyJson['tax_year'] = this.filterParams?.tax_year || this.globalService?.tax_year || '';
    dummyJson['scenario'] = this.filterParams?.scenario || this.globalService?.scenario || '';
    dummyJson['jcd_key'] = this.filterParams?.jcd_key || this.globalService?.jcd_key || '';

    const sendobj: any[] = [];
    sendobj.push(dummyJson);

    const message = "Chart has been Deleted";

    // Call delete service like AngularJS
    this.deleteChartCRUD(dummyJson, sendobj, message);
  }

  deleteChartCRUD(returnObj: any, accountArray: any[], message: string): void {
    // Follow exact AngularJS deletechartCRUD function behavior
    this.localChartDeleteService.saveDeleteChart(
      this.resolveApiBase(), // Defensive: fallback through GENERAL_CONFIG -> baseURLs
      returnObj, // Pass returnObj (dummyJson)
      accountArray, // Pass accountArray (sendobj)
      this.globalService, // Pass globalService like fir163jsync
      this.filterParams, // Pass filterParams
      this.rowData // Pass rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        this.crudLoading = false;
        this.modalConfig = {...this.modalConfig, loading: false};

        if (result.errorMessage && result.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(result.errorMessage, 'gtw-snackbar--red');
          this.cancel();
        } else {
          // Show success message (equivalent to AlertService.add("success", message, 4000))
          this.gtwSnackbarService.openSnackBar(message, 'gtw-snackbar--green');

          // CRITICAL: Emit 'refresh' string for AngularJS modal close
          this.requestGridUpdate.emit('refresh');
          this.cancel();
        }
      },
      (error: any) => {
        this.crudLoading = false;
        this.modalConfig = {...this.modalConfig, loading: false};
        this.gtwSnackbarService.openSnackBar('Error occurred while deleting. Please try again.', 'gtw-snackbar--red');
        this.cancel();
        console.error('Delete chart error:', error);
      }
    );
  }
}

